import { PopoverProps, PopperProps, Theme } from '@mui/material';
import { SystemStyleObject } from '@mui/system';
import { bloxValues } from 'themes/constants';
import { getPrimaryBgColor, getSolidThemeGray } from 'utility/styles';
import { radiusHalf } from 'utility/borderRadius';

// ------------------------------------------------
// Types & Constant Values
// ------------------------------------------------

export type InputChoice = {
  label: string;
  value: string;
  // optional - eg. for authors label could be the email, sublabel could be the name
  sublabel?: string;
  // optional - choice is exclusive and clears others when selected
  exclusive?: boolean;
  // optional - additional description info (eg: tooltips, CMSADMIN-2010)
  description?: string;
  // optional - misc additional data
  misc?: InputChoiceMisc;
  [key: string]: any;
};

type InputChoiceMisc = {
  // cycle selection action
  action?: 'add' | 'remove' | string | null;
  // any original item properties to store for reference
  item?: {
    [key: string]: any;
  };
  [key: string]: any;
};

// --- Users / Subscription ---
export const bannedChoices: InputChoice[] = [
  { label: 'Active', value: 'false' },
  { label: 'Banned', value: 'true' },
];

export const userStatusChoices: InputChoice[] = [
  { label: 'Active', value: 'active' },
  { label: 'Deleted', value: 'deleted' },
];

// "User" is the implicit default, we don't want to show always in the adv filters
// so we'll use one option in a multi-select as a 'checkbox' toggle instead for view_by
// eg implicit default is: { label: 'User', value: 'user' },
export const userSubViewByChoices: InputChoice[] = [
  { label: 'View By Subscription', value: 'subscription' },
];

export const viewByHelpText =
  'When this option is enabled a user may show up in the search results multiple times if the user has multiple subscription services.';

export const authTypeChoices: InputChoice[] = [
  { label: 'Password', value: 'password' },
  { label: 'Newzware', value: 'newzware' },
  { label: 'SAML', value: 'saml' },
  { label: 'Google', value: 'google' },
  { label: 'Facebook', value: 'facebook' },
  { label: 'OpenID Connect', value: 'openid connect' },
  { label: 'Apple ID', value: 'apple id' },
  { label: 'OpenID 2.0', value: 'openid 2.0' },
];

export const subscriptionStateChoices: InputChoice[] = [
  { label: 'Active', value: 'active' },
  { label: 'Grace', value: 'grace' },
  { label: 'Canceled', value: 'canceled' },
  { label: 'Former', value: 'former' },
  { label: 'Never', value: 'never' },
];

// --- User Journeys / Access Control ---
export const ruleTypeChoices: InputChoice[] = [
  { label: 'Web', value: 'web' },
  { label: 'App', value: 'app' },
  { label: 'AMP', value: 'amp' },
];

// If true, only show enabled rules, false/no value is show all rules
export const enabledRulesChoice: InputChoice[] = [
  { label: 'Enabled Only', value: 'true' },
];

// --- Editorial ---
export const advertorialChoices: InputChoice[] = [
  { label: 'Include', value: 'yes' },
  { label: 'Exclude', value: 'no' },
];

export const applicationChoices: InputChoice[] = [
  { label: 'Editorial', value: 'editorial' },
  { label: 'Business', value: 'business' },
  { label: 'Calendar', value: 'calendar' },
  { label: 'Classifieds', value: 'classifieds' },
  { label: 'E-Edition', value: 'eedition' },
];

export const userContributedChoices: InputChoice[] = [
  { label: 'Contributed', value: 'yes' },
  { label: 'Not Contributed', value: 'no' },
];

export const deletedContentChoices: InputChoice[] = [
  { label: 'Yes', value: '1' },
  { label: 'No', value: '0' },
];

export const draftContentChoices: InputChoice[] = [
  { label: 'Drafts only', value: 'yes' },
  { label: 'Publishable only', value: 'no' },
];

export const templateContentChoices: InputChoice[] = [
  { label: 'Yes', value: 'yes' },
  { label: 'No', value: 'no' },
];

export const flagsChoices: InputChoice[] = [
  { label: 'AP', value: 'ap' },
  { label: 'Advertorial', value: 'advertorial' },
  { label: 'Affiliate', value: 'affiliate' },
  { label: 'Agate', value: 'agate' },
  { label: 'Alert', value: 'alert' },
  { label: 'Breaking', value: 'breaking' },
  { label: 'Centerpiece', value: 'centerpiece' },
  { label: 'Contributed', value: 'contributed' },
  { label: 'Developing', value: 'developing' },
  { label: "Editor's Pick", value: 'editors_pick' },
  { label: 'Enterprise', value: 'enterprise' },
  { label: 'Exclusive', value: 'exclusive' },
  { label: 'Featured', value: 'featured' },
  { label: 'Hot', value: 'hot' },
  { label: 'Popular', value: 'popular' },
  { label: 'Promotion', value: 'promotion' },
  { label: 'Special Report', value: 'special_report' },
  { label: 'Special Section', value: 'special_section' },
  { label: 'Sponsored', value: 'sponsored' },
  { label: 'Spotlight', value: 'spotlight' },
  { label: 'Top Story', value: 'top_story' },
  { label: 'Topical', value: 'topical' },
  { label: 'Urgent', value: 'urgent' },
  { label: 'Watchdog', value: 'watchdog' },
  { label: 'Weather Alert', value: 'weather_alert' },
  { label: 'Web Only', value: 'web_only' },
  { label: 'Wire', value: 'wire' },
];

export const photoSalesChoices: InputChoice[] = [
  { label: 'Enabled', value: 'yes' },
  { label: 'Disabled', value: 'no' },
];

export const publicationStatusChoices: InputChoice[] = [
  { label: 'Placed', value: 'placed' },
  { label: 'Assigned', value: 'assigned' },
];

export const printReadyChoices: InputChoice[] = [
  { label: 'Yes', value: 'yes' },
  { label: 'No', value: 'no' },
];

export const publishStatusChoices: InputChoice[] = [
  { label: 'Published', value: 'published' },
  { label: 'Unpublished', value: 'unpublished' },
  { label: 'Awaiting Processing', value: 'awaiting_processing' },
];

export const contentTypeChoices: InputChoice[] = [
  { label: 'Article', value: 'article' },
  { label: 'Image', value: 'image' },
  { label: 'Collection', value: 'collection' },
  { label: 'Audio', value: 'audio' },
  { label: 'File', value: 'file' },
  { label: 'Flash', value: 'flash' },
  { label: 'HTML', value: 'html' },
  { label: 'Link', value: 'link' },
  { label: 'PDF', value: 'pdf' },
  { label: 'Poll', value: 'poll' },
  { label: 'Table', value: 'table' },
  { label: 'Video', value: 'video' },
  { label: 'YouTube', value: 'youtube' },
  { label: 'Zip', value: 'zip' },
];

// ------------------------------------------------
// Styles/Consts for Reusable Inputs
// ------------------------------------------------

export const getCheckboxSx = (theme: Theme): SystemStyleObject => ({
  margin: 0,
  py: 0,
  px: 0.5,
  fontSize: 16,
  '& svg': {
    fontSize: 16,
  },
});

export const getCheckboxTypoSx = (theme: Theme): SystemStyleObject => ({
  fontSize: bloxValues.smallFontSizeRem,
  whiteSpace: 'normal',
  wordBreak: 'break-word', // Ensures words break properly
  overflowWrap: 'break-word',
  wordWrap: 'break-word',
});

export const getCheckboxSubTypoSx = (theme: Theme): SystemStyleObject => ({
  fontSize: bloxValues.tinyFontSizeRem,
  whiteSpace: 'normal',
  wordBreak: 'break-word',
  overflowWrap: 'break-word',
  wordWrap: 'break-word',
});

export const getNoResultsSx = (theme: Theme): SystemStyleObject => ({
  p: 1,
  color: theme.palette.text.disabled,
  fontSize: bloxValues.smallFontSizeRem,
});

export const getFormControlSx = (
  theme: Theme,
  popoverOpen: boolean,
): SystemStyleObject => ({
  p: 0,
  m: 0,
  // override muis formctrl minwidth 0, / display
  // was causing overlap / truncation on mobile?
  width: 'auto',
  display: 'flex',
  fontSize: bloxValues.tinyFontSizeRem,
  borderRadius: 1,
  '&:hover': {
    bgcolor: theme.palette.action.hover,
  },
  ...(popoverOpen
    ? {
        bgcolor: getPrimaryBgColor(theme),
        '& .MuiInputBase-input': {
          color: theme.palette.primary.dark,
        },
      }
    : {}),
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.grey[500],
  },
  '& .MuiInput-underline:before': {
    borderBottom: 'none',
    borderColor: 'transparent',
  },
  '& .MuiInput-underline:after': {
    borderBottom: `none`,
    borderColor: 'transparent',
  },
  '&:hover .MuiInput-underline:before': {
    borderBottom: `none`,
    borderColor: 'transparent',
  },
  '& .MuiInputBase-root.MuiInput-underline:hover:not(.Mui-disabled, .Mui-error):before': {
    borderBottom: `none`,
    borderColor: 'transparent',
  },
  '& .MuiInputBase-root.MuiInput-underline:hover:not(.Mui-disabled, .Mui-error):after': {
    borderBottom: `none`,
    borderColor: 'transparent',
  },
});

export const getSelectSx = (theme: Theme): SystemStyleObject => ({
  minWidth: 50,
  borderRadius: 1,
  '& .MuiSelect-select': {
    borderRadius: 1,
    '&:focus': { borderRadius: 1 },
    p: 0.5,
  },
  '& .MuiSelect-select p': {
    pt: 0.75, // Center placeholder without shifting on mount
  },
});

export const getChipsRenderChipSx = (
  theme: Theme,
  isError: boolean,
  chipColor?: string,
): SystemStyleObject => {
  const isCustomColor = Boolean(chipColor);

  return {
    fontSize: bloxValues.tinyFontSizeRem,
    // Allow provided mui colors to display normally
    ...(isCustomColor
      ? {}
      : {
          color: isError ? theme.palette.error.main : theme.palette.text.primary,
          bgcolor: getSolidThemeGray(theme),
          outline: `1px solid ${
            isError ? theme.palette.error.main : theme.palette.divider
          }`,
          outlineOffset: '-1px',
        }),
    whiteSpace: 'normal',
    height: 'auto',
    '& .MuiChip-label': {
      fontWeight: isError
        ? theme.typography.fontWeightMedium
        : theme.typography.fontWeightRegular,
      display: 'block',
      whiteSpace: 'normal',
      wordBreak: 'break-word',
      px: 1,
      // explicit vertical padding keeps chip height stable across MUI version bumps (MUI v7 shrank the default)
      py: 0.5,
    },
  };
};

export const getChipsRenderTypoSx = (theme: Theme): SystemStyleObject => ({
  fontSize: bloxValues.tinyFontSizeRem,
  fontWeight: theme.typography.fontWeightRegular,
  color:
    theme.palette.mode === 'dark'
      ? theme.palette.text.secondary
      : theme.palette.text.disabled,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  wordBreak: 'normal',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 1,
});

export const getLabelSelectRenderChipSx = (theme: Theme): SystemStyleObject => ({
  height: 18,
  width: 18,
  fontSize: bloxValues.tinyFontSizeRem,
  fontWeight: theme.typography.fontWeightMedium,
  color: theme.palette.primary.contrastText,
  '& .MuiChip-label': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 1,
  },
});

export const getSelectRenderValueTypoSx = (theme: Theme): SystemStyleObject => ({
  fontSize: bloxValues.tinyFontSizeRem,
  fontWeight: theme.typography.fontWeightMedium,
  minWidth: 'max-content', // prevent label truncation, ellipsis if somehow forced to
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  wordBreak: 'normal',
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 1,
});

export const getHeaderLabelTypoSx = (theme: Theme): SystemStyleObject => ({
  fontSize: bloxValues.smallFontSizeRem,
  fontWeight: theme.typography.fontWeightMedium,
  color: theme.palette.text.secondary,
  display: 'flex', // flex for infotext icon if provided
  alignItems: 'center',
  gap: 0.5,
});

export const tooltipPopperProps: Partial<PopperProps> = {
  popperOptions: {
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [-10, -10],
        },
      },
    ],
  },
};

export const getInfoTextIconSx = (theme: Theme): SystemStyleObject => ({
  fontSize: bloxValues.regularFontSizeRem,
  fontWeight: theme.typography.fontWeightLight,
  color: theme.palette.text.secondary,
});

export const getRemoveButtonSx = (theme: Theme): SystemStyleObject => ({
  fontSize: bloxValues.tinyFontSizeRem,
  fontWeight: theme.typography.fontWeightRegular,
  lineHeight: 'normal',
  textTransform: 'none',
  minWidth: 'auto',
  maxWidth: 'auto',
  textAlign: 'left',
  alignItems: 'center',
  '& .MuiButton-startIcon': {
    mr: 0.25,
    ml: 0,
  },
  '& .MuiButton-startIcon .MuiSvgIcon-root': {
    fontSize: bloxValues.tinyFontSizeRem,
  },
});

export const getFilterTextfieldSx = (theme: Theme): SystemStyleObject => ({
  m: 0,
  p: 0,
  fontSize: bloxValues.smallFontSizeRem,
  borderRadius: 0,
  bgcolor: theme.palette.background.paper,
  overflow: 'auto',
  '& .MuiInputBase-root': {
    p: 0,
    m: 0,
  },
  '& .MuiInputBase-input': {
    fontSize: bloxValues.smallFontSizeRem,
    borderRadius: radiusHalf(theme),
    p: 0.5,
    m: 0,
  },
  '& .MuiInputAdornment-root': {
    cursor: 'pointer',
    fontSize: bloxValues.smallFontSizeRem,
    borderLeft: `1px solid ${theme.palette.divider}`,
    borderRight: 'none',
    borderTop: 'none',
    borderBottom: 'none',
    height: '100%',
    m: 0,
    py: 0,
    pl: 0,
    pr: 0.25,
    '&:hover': {
      bgcolor: theme.palette.action.hover,
    },
  },
});

export const getAccordionChipBoxSx = (theme: Theme): SystemStyleObject => ({
  m: 0,
  p: 0,
  bgcolor: getSolidThemeGray(theme),
  color: theme.palette.text.secondary,
  transition: theme.transitions.create(['margin', 'padding']),
  boxShadow: 'none',
  '& .Mui-expanded': {
    mb: 1,
    pb: 1,
    transition: theme.transitions.create(['margin', 'padding']),
  },
  '&.MuiAccordion-root:before': {
    display: 'none',
  },
  '& .MuiAccordionSummary-root': {
    display: 'flex',
    alignItems: 'center',
    gap: 0.5,
    borderRadius: '4px',

    '&:hover': {
      bgcolor: theme.palette.action.hover,
      outline: `2px solid ${theme.palette.action.hover}`,
      outlineOffset: '-2px',
    },

    '& .MuiAccordionSummary-content': {
      mx: 0.5,
      my: 0,
      px: 0,
      py: 0.5,
      flexGrow: 1,
      display: 'flex',
      alignItems: 'center',
      gap: 0.5,
    },
    '& .MuiAccordionSummary-expandIconWrapper': {
      m: 0,
      p: 0,
    },
  },
  '& .MuiAccordionDetails-root': {
    overflow: 'auto',
    maxHeight: '85px',
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 0.5,
    pt: 0.5,
    pb: 0,
    px: 0,
    m: 0,
  },
  '& .MuiButtonBase-root': {
    m: 0,
    p: 0,
    minHeight: 0,
  },
});

export const getExpandMoreIconSx = (theme: Theme): SystemStyleObject => ({
  fontSize: 12,
  width: 14,
  height: 14,
  '& svg': {
    fontSize: 12,
    width: 14,
    height: 14,
  },
});

export const getDividerDecoratorSx = (theme: Theme): SystemStyleObject => ({
  mr: 0.5,
  ml: 1,
  flexGrow: 1,
  borderTop: `1px solid ${theme.palette.action.selected}`,
});

export const getAccordionChipSx = (theme: Theme): SystemStyleObject => ({
  color: theme.palette.text.primary,
  fontSize: '0.688rem', // 11px
  height: 'auto',
  '& .MuiChip-label': {
    display: 'block',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    py: '2px',
  },
});
export const getFooterContainerSx = (
  theme: Theme,
  hasFooterItems: boolean,
): SystemStyleObject => ({
  background: getSolidThemeGray(theme),
  minHeight: hasFooterItems ? '28px' : undefined,
  borderTop: hasFooterItems ? `1px solid ${theme.palette.action.selected}` : undefined,
});

export const getFooterTypographySx = (theme: Theme): SystemStyleObject => ({
  fontSize: bloxValues.tinyFontSizeRem,
  color: theme.palette.text.disabled,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: 1,
});

export const getCloseButtonSx = (theme: Theme): SystemStyleObject => ({
  width: '100%',
  height: '100%',
  minHeight: '24px',
  px: 3,
  py: 0,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  borderLeft: `1px solid ${theme.palette.action.selected}`,
  fontSize: bloxValues.smallFontSizeRem,
  fontWeight: bloxValues.fontWeightMedium,
  color: theme.palette.text.secondary,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
});

export const getTooltipSx = (theme: Theme): SystemStyleObject => ({
  px: '4px',
  py: '2px',
  whiteSpace: 'pre-line', // break on '\n' for map joins
  fontSize: bloxValues.tinyFontSizeRem,
});

// ------------------------------------------------
// DROPDOWN STYLE SETS
// ------------------------------------------------

/**
 * FormControl styling for labels
 * Full-width outlined fields with visible labels
 */
export const getFormControlSxShowLabel = (theme: Theme): SystemStyleObject => ({
  ...getFormControlSx(theme, false),
  display: 'flex',
  width: '100%',
  mb: 0.5,
});

/**
 * Select styling for labels
 * Additional padding for label with chips to prevent overlap
 */
export const getSelectSxShowLabel = (theme: Theme): SystemStyleObject => ({
  ...getSelectSx(theme),
  pt: 0.75,
});

export const dropdownInputAnchorOrigin: PopoverProps['anchorOrigin'] = {
  vertical: 'bottom',
  horizontal: 'left',
};

export const dropdownInputTransformOrigin: PopoverProps['transformOrigin'] = {
  vertical: 'top',
  horizontal: 'left',
};

export const dropdownInputElevation: PopoverProps['elevation'] = 4;

export const getDropdownInputThreshold = (
  theme: Theme,
): PopoverProps['marginThreshold'] => {
  const minimums = 15; // px
  return minimums;
};

export const maxPopoverWidthPx = '275px';
export const maxPopoverWidthPxDateRange = '400px';
export const maxPopoverWidthPxRange = '350px';
export const maxPopoverHeightVh = '50vh';
export const filterTextDebounceMs = 350;
export const fiveMinutesMs = 300000; // 5m
